import React from 'react';
import {Marketer} from '../models/marketer.model';
import {UpdateMarketerProfileRequest} from '../models/UpdateMarketer';
import {FormField, FormSection, FormActions} from '../../../Presentation/components/common';
import {useFormState, CommonValidationRules, createFieldProps} from '../../../Presentation/utils/formUtils';
import Card from '../../../Presentation/components/ui/Card';
import Modal from '../../../Presentation/components/ui/Modal';
import {formatDate} from '../../../Presentation/utils/dateUtils';
import {buildInitialMarketerFormValues} from '../utils/marketerFormValues';

interface EditMarketerFormProps {
  marketer: Marketer;
  onSubmit: (marketerData: UpdateMarketerProfileRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  open: boolean;
}

const EditMarketerForm: React.FC<EditMarketerFormProps> = ({
  marketer,
  onSubmit,
  onCancel,
  loading = false,
  open
}) => {
  // Centralized form state
  const [formState, formActions] = useFormState(
    buildInitialMarketerFormValues(marketer),
    {
      fullName: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'الاسم الكامل مطلوب';
          }
          if (value.length < 2) {
            return 'الاسم الكامل يجب أن يكون حرفين على الأقل';
          }
          return null;
        }
      },
      email: CommonValidationRules.email,
      phoneNumber: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'رقم الهاتف مطلوب';
          }
          const phoneRegex = /^(\+966|966|0)?5[0-9]{8}$/;
          if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
            return 'صيغة رقم الهاتف غير صحيحة (يجب أن يكون رقم سعودي)';
          }
          return null;
        }
      }
    }
  );

  const handleSubmit = async (data: UpdateMarketerProfileRequest) => {
    try {
      // Clean phone number
      const cleanedData = {
        ...data,
        phoneNumber: data.phoneNumber.replace(/\s+/g, ''),
        email: data.email.toLowerCase().trim(),
        fullName: data.fullName.trim()
      };

      await onSubmit(cleanedData);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <Modal open={open} onClose={onCancel} title="تعديل بيانات المسوق">
      <form onSubmit={(e) => { e.preventDefault(); formActions.submit(handleSubmit); }} className="space-y-6">
        <FormSection title="بيانات المسوق">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              type="text"
              label="الاسم الكامل *"
              placeholder="أدخل الاسم الكامل"
              {...createFieldProps('fullName', formState, formActions)}
            />
            <FormField
              type="tel"
              label="رقم الهاتف *"
              placeholder="05xxxxxxxx"
              {...createFieldProps('phoneNumber', formState, formActions)}
            />
            <div className="md:col-span-2">
              <FormField
                type="email"
                label="البريد الإلكتروني *"
                placeholder="example@email.com"
                {...createFieldProps('email', formState, formActions)}
              />
            </div>
          </div>
        </FormSection>

        {/* معلومات المسوق الحالية */}
        <Card className="p-4 bg-gray-50">
          <h4 className="font-medium text-gray-700 mb-2">معلومات المسوق الحالية</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">اسم المستخدم:</span>
              <span className="font-medium mr-2">{marketer.username}</span>
            </div>
            <div>
              <span className="text-gray-500">الحالة:</span>
              <span className={`font-medium mr-2 ${marketer.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                {marketer.status === 'active' ? 'نشط' : 'غير نشط'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">الدور:</span>
              <span className="font-medium mr-2">
                {typeof marketer.role === 'string'
                  ? marketer.role
                  : (marketer.role as any)?.name || 'غير محدد'
                }
              </span>
            </div>
            <div>
              <span className="text-gray-500">تاريخ الإنشاء:</span>
              <span className="font-medium mr-2">
                {marketer.createdAt ? formatDate(marketer.createdAt.toISOString()) : 'غير محدد'}
              </span>
            </div>

            {/* Location Information (Read-only) */}
            {(marketer.latitude || marketer.longitude || marketer.territory || marketer.vehicleType) && (
              <>
                <div className="md:col-span-2 border-t border-gray-200 pt-3 mt-2">
                  <h5 className="font-medium text-gray-600 mb-2">معلومات الموقع</h5>
                </div>
                {marketer.latitude && (
                  <div>
                    <span className="text-gray-500">خط العرض:</span>
                    <span className="font-medium mr-2">{marketer.latitude.toFixed(6)}</span>
                  </div>
                )}
                {marketer.longitude && (
                  <div>
                    <span className="text-gray-500">خط الطول:</span>
                    <span className="font-medium mr-2">{marketer.longitude.toFixed(6)}</span>
                  </div>
                )}
                {marketer.territory && (
                  <div>
                    <span className="text-gray-500">المنطقة:</span>
                    <span className="font-medium mr-2">{marketer.territory}</span>
                  </div>
                )}
                {marketer.vehicleType && (
                  <div>
                    <span className="text-gray-500">نوع المركبة:</span>
                    <span className="font-medium mr-2">{marketer.vehicleType}</span>
                  </div>
                )}
                {marketer.lastLocationUpdate && (
                  <div>
                    <span className="text-gray-500">آخر تحديث للموقع:</span>
                    <span className="font-medium mr-2">
                      {formatDate(marketer.lastLocationUpdate.toISOString())}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        <FormActions
          onSubmit={() => formActions.submit(handleSubmit)}
          onCancel={onCancel}
          submitLabel="تحديث البيانات"
          cancelLabel="إلغاء"
          isLoading={loading || formState.isSubmitting}
          disabled={!formState.isValid}
          showReset={false}
        />
      </form>
    </Modal>
  );
};

export default EditMarketerForm;
